import React, { useEffect, useRef, useState, useCallback } from "react";
import { Chessground } from "chessground";
import "./board.css";
import { playMove, playCapture, playCheck, playGameEnd } from "../sounds";

// Helpers -----------------------------------------------------------------

// Build the Map<from, to[]> that chessground expects for legal move hints
// from the current chess.js position.
function buildLegalDests(chess) {
    const dests = new Map();
    if (!chess) return dests;
    let moves = [];
    try { moves = chess.moves({ verbose: true }) || []; }
    catch { return dests; }
    for (const m of moves) {
        if (!dests.has(m.from)) dests.set(m.from, []);
        dests.get(m.from).push(m.to);
    }
    return dests;
}

// Support older chess.js that uses in_check() vs newer isCheck().
function checkedColor(chess) {
    if (!chess) return undefined;
    let inCheck = false;
    try { inCheck = typeof chess.isCheck === "function" ? chess.isCheck() : false; } catch { }
    if (!inCheck) {
        try { inCheck = typeof chess.in_check === "function" ? chess.in_check() : false; } catch { }
    }
    if (!inCheck) return undefined;
    return chess.turn() === "w" ? "white" : "black";
}

function turnColor(chess) {
    if (!chess) return "white";
    return chess.turn() === "w" ? "white" : "black";
}

// chess.js history({verbose:true}) returns the list of played moves; take the
// most recent one so chessground can highlight the last-move squares.
function lastMove(chess) {
    if (!chess) return undefined;
    let hist = [];
    try { hist = chess.history({ verbose: true }) || []; } catch { return undefined; }
    if (hist.length === 0) return undefined;
    const last = hist[hist.length - 1];
    return [last.from, last.to];
}

// Default responsive width so callers don't have to provide calcWidth.
function defaultWidth({ screenWidth, screenHeight }) {
    return screenWidth <= 768
        ? Math.min(500, screenWidth - 32)   // 32 matches board-container max-width: calc(100vw - 32px)
        : Math.min(700, screenHeight - 170, screenWidth - 32);
}

// Component ---------------------------------------------------------------

const Board = ({
    onMove,
    chess,
    currFen,
    side,
    notation,
    customOnDrop,
    draggable,
    calcWidth,
    bestMoveArrow,
    secondMoveArrow,
    sounds = false,
    premoves = false,
}) => {
    const wrapRef = useRef(null);
    const cgRef = useRef(null);
    // Compute synchronously so Chessground always mounts at the correct size.
    // If width starts at 0, Chessground caches a 0×0 bounding rect and tap
    // coordinates are wrong until ResizeObserver fires — unreliable on mobile.
    const calcW = calcWidth || defaultWidth;
    const [width, setWidth] = useState(() =>
        Math.round(calcW({ screenWidth: window.innerWidth, screenHeight: window.innerHeight }))
    );

    // Keep callbacks in refs so we don't have to tear down chessground to swap
    // handler identities (inline arrows from parents change every render).
    const customOnDropRef = useRef(customOnDrop);
    const onMoveRef = useRef(onMove);
    useEffect(() => { customOnDropRef.current = customOnDrop; }, [customOnDrop]);
    useEffect(() => { onMoveRef.current = onMove; }, [onMove]);

    // Track move count so we play a sound exactly once per real move and
    // don't replay when the prop-sync useEffect fires for unrelated reasons.
    const moveCountRef = useRef(0);
    const soundsRef = useRef(sounds);
    useEffect(() => { soundsRef.current = sounds; }, [sounds]);

    const playMoveSound = useCallback((c) => {
        if (!soundsRef.current || !c) return;
        const newCount = (() => {
            try { return c.history().length; } catch { return 0; }
        })();
        // Only fire when history grew by exactly one — protects against
        // history shrinking (e.g., reset) or jumps (analysis navigation).
        if (newCount !== moveCountRef.current + 1) {
            moveCountRef.current = newCount;
            return;
        }
        moveCountRef.current = newCount;
        let last = null;
        try { last = c.history({ verbose: true })[newCount - 1]; } catch { return; }
        if (!last) return;
        const isOver =
            (typeof c.isCheckmate === "function" && c.isCheckmate()) ||
            (typeof c.isDraw === "function" && c.isDraw()) ||
            (typeof c.in_checkmate === "function" && c.in_checkmate()) ||
            (typeof c.in_draw === "function" && c.in_draw());
        const isCheck =
            (typeof c.isCheck === "function" && c.isCheck()) ||
            (typeof c.in_check === "function" && c.in_check());
        if (isOver) playGameEnd();
        else if (isCheck) playCheck();
        else if (last.captured) playCapture();
        else playMove();
    }, []);

    const recalcWidth = useCallback(() => {
        const w = calcW({ screenWidth: window.innerWidth, screenHeight: window.innerHeight });
        setWidth(Math.round(w));
    }, [calcW]);

    useEffect(() => {
        recalcWidth();
        window.addEventListener("resize", recalcWidth);
        return () => window.removeEventListener("resize", recalcWidth);
    }, [recalcWidth]);

    // Mount chessground once. All later changes go through cg.set({...}).
    useEffect(() => {
        if (!wrapRef.current) return undefined;
        const isDraggable = draggable !== false;
        const ground = Chessground(wrapRef.current, {
            fen: currFen && currFen !== "start" ? currFen : undefined,
            orientation: side === "black" ? "black" : "white",
            coordinates: notation !== false,
            turnColor: turnColor(chess),
            check: checkedColor(chess),
            lastMove: lastMove(chess),
            highlight: { lastMove: true, check: true },
            animation: { enabled: true, duration: 200 },
            draggable: {
                enabled: isDraggable,
                distance: 3,
                autoDistance: true,
                showGhost: true,
            },
            selectable: { enabled: true },
            premovable: {
                enabled: !!premoves,
                showDests: true,
                castle: true,
            },
            movable: {
                free: false,
                color: isDraggable ? turnColor(chess) : undefined,
                dests: buildLegalDests(chess),
                showDests: true,
                events: {
                    after: (from, to) => {
                        // If the parent handed us a custom drop handler (GameApp), let it
                        // deal with the move (including pending-promotion state). Otherwise
                        // apply the move locally via chess.js and notify via onMove.
                        const custom = customOnDropRef.current;
                        if (custom) {
                            custom(from, to);
                            // Snap the board back to the external fen so chessground doesn't
                            // optimistically keep the piece on the target square for pending
                            // promotions. Parent will push the new fen via props when ready.
                            if (cgRef.current && chess) {
                                cgRef.current.set({
                                    fen: chess.fen(),
                                    turnColor: turnColor(chess),
                                    check: checkedColor(chess),
                                    lastMove: lastMove(chess),
                                    movable: {
                                        color: isDraggable ? turnColor(chess) : undefined,
                                        dests: buildLegalDests(chess),
                                    },
                                });
                            }
                            // Play sound immediately for our own move; opponent moves are
                            // covered by the prop-sync useEffect when their fen arrives.
                            playMoveSound(chess);
                            return;
                        }
                        if (!chess) return;
                        let move = null;
                        try { move = chess.move({ from, to, promotion: "q" }); }
                        catch { move = null; }
                        if (!move) {
                            // Illegal move — resync board from source of truth.
                            if (cgRef.current) {
                                cgRef.current.set({
                                    fen: chess.fen(),
                                    turnColor: turnColor(chess),
                                    movable: {
                                        color: isDraggable ? turnColor(chess) : undefined,
                                        dests: buildLegalDests(chess),
                                    },
                                });
                            }
                            return;
                        }
                        if (cgRef.current) {
                            cgRef.current.set({
                                fen: chess.fen(),
                                turnColor: turnColor(chess),
                                check: checkedColor(chess),
                                lastMove: [from, to],
                                movable: {
                                    color: isDraggable ? turnColor(chess) : undefined,
                                    dests: buildLegalDests(chess),
                                },
                            });
                        }
                        const cb = onMoveRef.current;
                        if (cb) cb(chess.fen(), move);
                        playMoveSound(chess);
                    },
                },
            },
            drawable: {
                enabled: true,
                visible: true,
                defaultSnapToValidMove: true,
                autoShapes: [],
                // Custom brushes so the second-best move arrow is the same green
                // as the best move, just at lower opacity. Chessground's built-in
                // "yellow" defaults to orange which reads as a different move class.
                brushes: {
                    green: { key: 'g', color: '#15781B', opacity: 1, lineWidth: 10 },
                    red: { key: 'r', color: '#882020', opacity: 1, lineWidth: 10 },
                    blue: { key: 'b', color: '#003088', opacity: 1, lineWidth: 10 },
                    yellow: { key: 'y', color: '#e68f00', opacity: 1, lineWidth: 10 },
                    paleGreen: { key: 'pg', color: '#15781B', opacity: 0.4, lineWidth: 10 },
                },
            },
        });
        cgRef.current = ground;
        return () => {
            ground.destroy();
            cgRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep chessground in sync with controlled props.
    const prevNotationRef = useRef(notation);
    useEffect(() => {
        const cg = cgRef.current;
        if (!cg) return;
        const isDraggable = draggable !== false;
        cg.set({
            fen: currFen && currFen !== "start" ? currFen : undefined,
            orientation: side === "black" ? "black" : "white",
            coordinates: notation !== false,
            turnColor: turnColor(chess),
            check: checkedColor(chess),
            lastMove: lastMove(chess),
            draggable: { enabled: isDraggable },
            movable: {
                color: isDraggable ? turnColor(chess) : undefined,
                dests: buildLegalDests(chess),
            },
        });
        // set() doesn't add/remove coordinate elements — only a full redraw does.
        if (prevNotationRef.current !== notation) {
            prevNotationRef.current = notation;
            try { cg.redrawAll(); } catch {}
        }
        // Play sound for moves that arrived via prop change (opponent's move,
        // or analysis navigation). playMoveSound dedupes via moveCountRef.
        playMoveSound(chess);
        // If a premove was queued earlier and it's now our turn, try to play
        // it. No-op if the premove is illegal in the new position or none set.
        try { cg.playPremove(); } catch {}
    }, [currFen, side, notation, chess, draggable, playMoveSound]);

    // Chessground memoizes its bounding rect and only refreshes it on resize
    // or scroll. Layout shifts that MOVE the board without resizing it (e.g.
    // the chat/moves panel mounting when a game becomes ready) leave the memo
    // stale, so pointer events map to the wrong squares. Clear the memo right
    // before chessground handles any pointer-down (capture phase runs first);
    // it recomputes the rect lazily, so this costs one getBoundingClientRect
    // per interaction.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const clearBounds = () => {
            try { cgRef.current?.state.dom.bounds.clear(); } catch {}
        };
        el.addEventListener("mousedown", clearBounds, { capture: true });
        el.addEventListener("touchstart", clearBounds, { capture: true, passive: true });
        return () => {
            el.removeEventListener("mousedown", clearBounds, { capture: true });
            el.removeEventListener("touchstart", clearBounds, { capture: true });
        };
    }, []);

    // Analysis arrows (best move green, second best yellow).
    useEffect(() => {
        const cg = cgRef.current;
        if (!cg) return;
        const shapes = [];
        if (secondMoveArrow && secondMoveArrow.from && secondMoveArrow.to) {
            shapes.push({
                orig: secondMoveArrow.from,
                dest: secondMoveArrow.to,
                brush: "paleGreen",
            });
        }
        if (bestMoveArrow && bestMoveArrow.from && bestMoveArrow.to) {
            shapes.push({
                orig: bestMoveArrow.from,
                dest: bestMoveArrow.to,
                brush: "green",
            });
        }
        cg.setAutoShapes(shapes);
    }, [bestMoveArrow, secondMoveArrow]);

    return (
        <div
            className="chess64-board-wrap"
            style={{ width: width || undefined, height: width || undefined }}
        >
            <div ref={wrapRef} style={{ width: "100%", height: "100%" }} />
        </div>
    );
};

// Memoize so parent re-renders (like the 100ms clock tick) don't trigger a
// full board rebuild.
export default React.memo(Board);
